import { reactive, ref } from 'vue';
import { storeToRefs } from 'pinia';
import { getFreeGiftCartSku, useCartStore } from '@/features/Cart/stores/cart';
import { matchesAnyBaseSku } from '@/composables/productHelper';
import { useRandomisation } from '@/features/Product/composables/useRandomisation';
import { useMysteryProduct } from '@/features/Product/composables/useMysteryProduct';
import { getProductBySku } from '@/features/Product/composables/productLookupHelper';
import { useProductStockStore } from '@/features/Product/stores/productStock';
import {
  findVariantParentMaxQuantityFamilies,
  getFamilyTotalQuantity,
} from './variantParentMaxQuantity';
import type { AtcObjectRedraw, CartRule, CartRuleResult } from '@/features/Cart/types/models';
import type {
  AddToCartItem,
  ShopifyCart,
  ShopifyCartItem,
} from '@/features/Cart/types/servicesShopifyCart';
import type {
  BundleDetails,
  CheckoutProductSelection,
  ProductComplete,
  RandomisationDetails,
  RandomisationException,
} from '@/features/Product/types/models';
import type { FreeGiftOption } from '@/features/Page/data/globalData';

export const SECOND_FREE_GIFT_ELIGIBLE_ATTRIBUTE = '_second_gift_eligible';

export type FreeGiftAddition = {
  item: AddToCartItem;
  redrawAdd?: AtcObjectRedraw;
};

const getCappedFamilies = (baselineItems: ShopifyCartItem[]) =>
  findVariantParentMaxQuantityFamilies().filter(
    (family) => getFamilyTotalQuantity(baselineItems, family) >= family.maxQuantity,
  );

const getExceededFamilies = (items: ShopifyCartItem[]) =>
  findVariantParentMaxQuantityFamilies().filter(
    (family) => getFamilyTotalQuantity(items, family) > family.maxQuantity,
  );

const excludeCappedFamilySkus = (
  randomisationDetails: RandomisationDetails | undefined,
  baselineItems: ShopifyCartItem[],
): RandomisationDetails | undefined => {
  if (!randomisationDetails?.options?.length) return randomisationDetails;
  const cappedFamilies = getCappedFamilies(baselineItems);
  if (!cappedFamilies.length) return randomisationDetails;
  return {
    ...randomisationDetails,
    options: randomisationDetails.options.filter(
      (option) =>
        !cappedFamilies.some((family) => matchesAnyBaseSku(option.sku, family.memberSkus)),
    ),
  };
};

const giftItemNeedsReroll = (item: ShopifyCartItem, cartItems: ShopifyCartItem[]): boolean => {
  const customBundleDetails = item.properties?._custom_bundle_details;
  if (!customBundleDetails) return false;
  const overCapacityFamilies = getExceededFamilies(cartItems);
  if (!overCapacityFamilies.length) return false;
  try {
    const skus: string[] = JSON.parse(customBundleDetails).map(
      (detail: { sku: string }) => detail.sku,
    );
    return skus.some((sku) =>
      overCapacityFamilies.some((family) => matchesAnyBaseSku(sku, family.memberSkus)),
    );
  } catch {
    return false;
  }
};

const unpublishedGiftSkus = reactive(new Set<string>());

const getGiftLineProductSkus = (item: ShopifyCartItem): string[] => {
  const customBundleDetails = item.properties?._custom_bundle_details;
  if (!customBundleDetails) return [item.sku];
  try {
    return JSON.parse(customBundleDetails).map((detail: { sku: string }) => detail.sku);
  } catch {
    return [];
  }
};

const isGiftProductSoldOut = (product: ProductComplete): boolean =>
  product.attributes.manualSoldOut || useProductStockStore().isProductSoldOut(product);

const isGiftItemUnavailable = (item: ShopifyCartItem): boolean => {
  const { unavailableLines, getItemStockLimit } = useProductStockStore();
  if (unavailableLines.includes(item.key)) return true;
  const stockLimit = getItemStockLimit({ variantId: item.variant_id, properties: item.properties });
  if (stockLimit !== undefined && stockLimit < item.quantity) return true;
  return getGiftLineProductSkus(item).some((sku) => {
    const product = getProductBySku(sku);
    return !!product && isGiftProductSoldOut(product);
  });
};

const giftItemExceptionMismatch = (
  item: ShopifyCartItem,
  exception: RandomisationException | undefined,
  exceptionApplies: boolean,
): boolean => {
  if (!exception) return false;
  const customBundleDetails = item.properties?._custom_bundle_details;
  if (!customBundleDetails) return false;
  try {
    const skus: string[] = JSON.parse(customBundleDetails).map(
      (detail: { sku: string }) => detail.sku,
    );
    return skus.includes(exception.assignedSku.sku) !== exceptionApplies;
  } catch {
    return false;
  }
};

const buildFixedBundleCustomDetails = (
  bundleDefinition: BundleDetails,
): CheckoutProductSelection[] =>
  bundleDefinition.bundleItems
    .filter((item) => item.itemType === 'fixed')
    .flatMap((item) =>
      (item.processedProducts || []).map((product) => ({
        id: `gid://shopify/ProductVariant/${product.attributes.variantId}` as const,
        sku: product.attributes.sku,
        quantity: item.quantity || 1,
      })),
    );

const getGiftException = (option: FreeGiftOption): RandomisationException | undefined =>
  getProductBySku(getFreeGiftCartSku(option))?.bundleDefinition?.additionalBundleDetails?.exception;

const isExceptionApplied = (
  exception: RandomisationException | undefined,
  otherCartProductHandles: string[],
): boolean =>
  !!exception &&
  otherCartProductHandles.every((handle) => exception.productHandles.includes(handle));

const getGiftStockSku = (option: FreeGiftOption): string =>
  option.bundleItemSku ?? getFreeGiftCartSku(option);

export const isFreeGiftOptionAvailable = (option: FreeGiftOption): boolean => {
  if (!getProductBySku(getFreeGiftCartSku(option))?.attributes.variantId) return false;
  const stockSku = getGiftStockSku(option);
  const stockProduct = getProductBySku(stockSku);
  if (!stockProduct?.attributes.variantId || unpublishedGiftSkus.has(stockSku)) return false;
  if (isGiftProductSoldOut(stockProduct)) return false;
  const displayProduct = getProductBySku(option.displaySku);
  return !displayProduct || !isGiftProductSoldOut(displayProduct);
};

export const buildFreeGiftAddition = (
  option: FreeGiftOption,
  nonGiftItems: ShopifyCartItem[],
  extraProperties: Record<string, string> = {},
): FreeGiftAddition | null => {
  const cartSku = getFreeGiftCartSku(option);
  const giftProduct = getProductBySku(cartSku);
  const variantId = giftProduct?.attributes.variantId;
  if (!variantId) {
    console.warn(`Free gift promo: could not find variantId for SKU ${cartSku}`);
    return null;
  }

  const baseItem: AddToCartItem = {
    id: variantId,
    quantity: 1,
    properties: {
      _free_gift: 'true',
      _free_gift_display_sku: option.displaySku,
      _max_cart_quantity: '1',
      ...extraProperties,
    },
  };

  if (option.bundleItemSku) {
    const bundleItemVariantId = getProductBySku(option.bundleItemSku)?.attributes.variantId;
    if (!bundleItemVariantId) return null;
    const bundleItem: CheckoutProductSelection[] = [
      {
        id: `gid://shopify/ProductVariant/${bundleItemVariantId}`,
        sku: option.bundleItemSku,
        quantity: 1,
      },
    ];
    return {
      item: {
        ...baseItem,
        properties: { ...baseItem.properties, _custom_bundle_details: JSON.stringify(bundleItem) },
      },
    };
  }

  const bundleDefinition = giftProduct.bundleDefinition;
  if (!bundleDefinition) return { item: baseItem };

  const itemWithBundleDetails: AddToCartItem = {
    ...baseItem,
    properties: {
      ...baseItem.properties,
      _custom_bundle_details: JSON.stringify(buildFixedBundleCustomDetails(bundleDefinition)),
    },
  };
  const { randomisation, exception, mysteryProduct } =
    bundleDefinition.additionalBundleDetails ?? {};
  const { resolveRandomisationAtcObject, redrawMysteryAtcObject } = useRandomisation(
    ref(excludeCappedFamilySkus(randomisation, nonGiftItems)),
    ref(exception),
    ref(nonGiftItems.map((item) => item.handle)),
  );
  const { resolveMysteryAtcObject } = useMysteryProduct(ref(mysteryProduct));
  const [resolvedItem] = resolveMysteryAtcObject(
    resolveRandomisationAtcObject([itemWithBundleDetails]),
  );
  if (!resolvedItem) return null;

  return {
    item: resolvedItem,
    redrawAdd: (items, variantIds) =>
      resolveMysteryAtcObject(redrawMysteryAtcObject(items, variantIds)),
  };
};

const getPrimaryFreeGiftChanges: CartRule = (cart) => {
  const cartStore = useCartStore();
  const { isFreeGiftItem, isSecondFreeGiftItem, getFreeGiftOption, isFreeGiftEligibleSku } =
    cartStore;
  const { freeGiftOptions, chosenFreeGiftSku, resolvedFreeGiftProduct } = storeToRefs(cartStore);

  const giftItems = cart.items.filter(
    (item) => isFreeGiftItem(item) && !isSecondFreeGiftItem(item),
  );
  const normalCartItems = cart.items.filter((item) => !isFreeGiftItem(item));
  const hasQualifyingItem = resolvedFreeGiftProduct.value?.requiresEligibleItem
    ? normalCartItems.some((item) => isFreeGiftEligibleSku(item.sku))
    : normalCartItems.length > 0;
  if (!freeGiftOptions.value.length || !hasQualifyingItem) {
    return {
      add: [],
      update: [],
      remove: giftItems.map((item) => ({ key: item.key, quantity: 0 })),
    };
  }

  const otherCartProductHandles = normalCartItems.map((item) => item.handle);
  const giftItemNeedsReplacing = (item: ShopifyCartItem): boolean => {
    const option = getFreeGiftOption(item);
    if (!option) return true;
    const exception = getGiftException(option);
    return (
      giftItemNeedsReroll(item, cart.items) ||
      giftItemExceptionMismatch(
        item,
        exception,
        isExceptionApplied(exception, otherCartProductHandles),
      ) ||
      isGiftItemUnavailable(item)
    );
  };

  const primaryGiftItem =
    giftItems.find((item) => getFreeGiftOption(item)?.displaySku === chosenFreeGiftSku.value) ??
    giftItems.find((item) => !!getFreeGiftOption(item)) ??
    giftItems[0];
  const duplicateGiftItems = giftItems.filter((item) => item !== primaryGiftItem);
  const needsReroll = !!primaryGiftItem && giftItemNeedsReplacing(primaryGiftItem);

  if (primaryGiftItem && !needsReroll) {
    return {
      add: [],
      update: primaryGiftItem.quantity > 1 ? [{ key: primaryGiftItem.key, quantity: 1 }] : [],
      remove: duplicateGiftItems.map((item) => ({ key: item.key, quantity: 0 })),
    };
  }

  const staleGiftItems = needsReroll ? giftItems : duplicateGiftItems;
  const removeStaleGiftItems = staleGiftItems.map((item) => ({ key: item.key, quantity: 0 }));

  const findOption = (displaySku: string | null) =>
    freeGiftOptions.value.find((option) => option.displaySku === displaySku);
  const optionToAdd = [
    primaryGiftItem && getFreeGiftOption(primaryGiftItem),
    findOption(chosenFreeGiftSku.value),
    ...freeGiftOptions.value,
  ].find((option): option is FreeGiftOption => !!option && isFreeGiftOptionAvailable(option));
  const addition = optionToAdd ? buildFreeGiftAddition(optionToAdd, normalCartItems) : null;

  if (!addition) {
    return removeStaleGiftItems.length
      ? { add: [], update: [], remove: removeStaleGiftItems }
      : null;
  }

  return {
    add: [addition.item],
    update: [],
    remove: removeStaleGiftItems,
    redrawAdd: addition.redrawAdd,
  };
};

const getSecondFreeGiftChanges = (
  cart: ShopifyCart,
  hasPrimaryGift: boolean,
): CartRuleResult | null => {
  const cartStore = useCartStore();
  const { isFreeGiftItem, isSecondFreeGiftItem, getFreeGiftOption } = cartStore;
  const { secondFreeGiftOption } = storeToRefs(cartStore);

  const option = secondFreeGiftOption.value;
  const isEligibleCart = cart.attributes?.[SECOND_FREE_GIFT_ELIGIBLE_ATTRIBUTE] === 'true';
  const isGiftDue =
    hasPrimaryGift && isEligibleCart && !!option && isFreeGiftOptionAvailable(option);
  const secondGiftItems = cart.items.filter(isSecondFreeGiftItem);
  const keptGiftItem = isGiftDue
    ? secondGiftItems.find((item) => !!getFreeGiftOption(item) && !isGiftItemUnavailable(item))
    : undefined;
  const remove = secondGiftItems
    .filter((item) => item !== keptGiftItem)
    .map((item) => ({ key: item.key, quantity: 0 }));
  const update =
    keptGiftItem && keptGiftItem.quantity > 1 ? [{ key: keptGiftItem.key, quantity: 1 }] : [];
  const addition =
    option && isGiftDue && !keptGiftItem
      ? buildFreeGiftAddition(
          option,
          cart.items.filter((item) => !isFreeGiftItem(item)),
          { _second_free_gift: 'true' },
        )
      : null;

  if (!addition && !update.length && !remove.length) return null;
  return { add: addition ? [addition.item] : [], update, remove };
};

export const cartRuleFreeGift: CartRule = (cart) => {
  const { isFreeGiftItem, isSecondFreeGiftItem, getFreeGiftOption } = useCartStore();
  const { unavailableLines } = useProductStockStore();
  cart.items
    .filter((item) => isFreeGiftItem(item) && unavailableLines.includes(item.key))
    .forEach((item) => {
      const option = getFreeGiftOption(item);
      unpublishedGiftSkus.add(option ? getGiftStockSku(option) : item.sku);
    });

  const primaryChanges = getPrimaryFreeGiftChanges(cart);
  const removedKeys = new Set(primaryChanges?.remove.map(({ key }) => key));
  const hasPrimaryGift =
    !!primaryChanges?.add.length ||
    cart.items.some(
      (item) => isFreeGiftItem(item) && !isSecondFreeGiftItem(item) && !removedKeys.has(item.key),
    );
  const secondChanges = getSecondFreeGiftChanges(cart, hasPrimaryGift);

  if (!secondChanges) return primaryChanges;
  if (!primaryChanges) return secondChanges;
  return {
    add: [...primaryChanges.add, ...secondChanges.add],
    update: [...primaryChanges.update, ...secondChanges.update],
    remove: [...primaryChanges.remove, ...secondChanges.remove],
    redrawAdd: primaryChanges.redrawAdd,
  };
};
